import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { useForm } from 'react-typed-form';

import { Api, useApiCallable, useApiFormSubmit } from '../../api';
import {
  AccentTextLink,
  Avatar,
  DateWithTooltip,
  Spacer,
  Text,
} from '../../components';
import { Style } from '../../constants';
import {
  FieldText,
  FieldTextRequired,
  FormWrap,
  SubmitButton,
} from '../../form';
import { useMyUserClient } from '../../hooks';
import { Model } from '../../models';
import BuildEvents from './BuildEvents';

type Props = Readonly<{
  build: {
    ['@id']: string;
    ['@type']: 'Build';
    id: string;
    buildNumber: number;
    environment: string;
    platform: string | null;
    link: string;
    qaApprovedAt: string | null;
    qaApprovedBy: Api.User.BaseView | null;
    expired: boolean;
    createdAt: string;
    createdBy: Api.User.BaseView;
    buildEvents: Array<{
      ['@id']: string;
      ['@type']: 'BuildEvent';
      id: string;
      type: string;
      percent: number | null;
      recordedAt: string;
    }>;
  };
  status: Model.Build.BuildStatus;
  invalidate: () => unknown;
}>;

export default function BuildDetail({ build, status, invalidate }: Props) {
  const callApi = useApiCallable();
  const myUser = useMyUserClient();

  const [editing, setEditing] = React.useState(false);

  type FormValues = Omit<Api.Build.W, 'buildNumber'> & { buildNumber: string };

  const formSubmit = useApiFormSubmit('PUT', `/builds/${build.id}`, {
    invalidate,
  });
  const form = useForm<Partial<FormValues>>({
    defaultValues: {},
    pristineValues: {
      buildNumber: String(build.buildNumber),
      environment: build.environment,
      platform: build.platform,
      link: build.link,
      qaApprovedAt: build.qaApprovedAt,
      qaApprovedBy: build.qaApprovedBy?.['@id'] ?? null,
      expired: build.expired,
    },
    onSubmit: (values, helpers) =>
      formSubmit(
        { ...values, buildNumber: parseInt(values.buildNumber ?? '', 10) },
        helpers
      ),
  });

  if (editing) {
    return (
      <FormWrap
        formErrorList={form.formErrorList}
        handleSubmit={form.handleSubmit}
      >
        <FieldTextRequired field={form.getField('buildNumber')} />
        <FieldText field={form.getField('platform')} />
        <FieldTextRequired field={form.getField('environment')} />
        <FieldTextRequired field={form.getField('link')} />
        <SubmitButton {...form} label="Submit" />{' '}
        <html.a
          style={styles.cancel}
          onClick={() => {
            setEditing(false);
            form.reset();
          }}
        >
          <Text>Cancel</Text>
        </html.a>
      </FormWrap>
    );
  }

  let linkPart: React.ReactNode = (
    <AccentTextLink
      href={build.link}
      target="_blank"
      rel="noreferrer"
      style={styles.externalLink}
    >
      {build.link}
    </AccentTextLink>
  );
  if (build.link === 'testflight') {
    linkPart = 'On TestFlight';
  } else if (build.link === 'playstore') {
    linkPart = 'On Play Store Test Track';
  }

  let qaStatus = (
    <html.div>
      <Text>Ready to test</Text>
      <html.div>
        <AccentTextLink
          onClick={async () => {
            await callApi(`builds/${build.id}/qa-approve`, {
              method: 'POST',
            });
            invalidate();
          }}
        >
          <Text>Approve</Text>
        </AccentTextLink>
      </html.div>
    </html.div>
  );
  if (build.qaApprovedAt) {
    qaStatus = (
      <>
        {build.qaApprovedBy && (
          <>
            <Avatar user={build.qaApprovedBy} size={30} />
            <Spacer size={0.25} />
          </>
        )}
        <html.div>
          <Text>Approved</Text>
          <Text as="p" style={styles.timestamp}>
            <DateWithTooltip timestamp={build.qaApprovedAt} />
          </Text>
        </html.div>
      </>
    );
  }

  return (
    <html.div style={[styles.container, styles.statusBorder(status)]}>
      <html.div style={styles.stats}>
        <html.div>
          <Text style={styles.statLabel}>Build number</Text>
          <Text as="strong" bold style={styles.statValue}>
            {build.buildNumber}
          </Text>
        </html.div>
        <html.div>
          <Text style={styles.statLabel}>Platform</Text>
          <Text as="strong" bold style={styles.statValue}>
            {build.platform}
          </Text>
        </html.div>
        <html.div>
          <Text style={styles.statLabel}>Environment</Text>
          <Text as="strong" bold style={styles.statValue}>
            {build.environment}
          </Text>
        </html.div>
        {myUser.agencyRole && (
          <>
            <html.div>
              <Text style={styles.statLabel}>Dev status</Text>
              <html.strong
                style={[styles.statValue, Style.row, Style.alignCenter]}
              >
                <Avatar user={build.createdBy} size={30} />
                <Spacer size={0.25} />
                <html.div>
                  <Text>Created</Text>
                  <Text as="p" style={styles.timestamp}>
                    <DateWithTooltip timestamp={build.createdAt} />
                  </Text>
                </html.div>
              </html.strong>
            </html.div>
            <html.div>
              <Text style={styles.statLabel}>QA status</Text>
              <html.strong
                style={[styles.statValue, Style.row, Style.alignCenter]}
              >
                {qaStatus}
              </html.strong>
            </html.div>
            <html.div style={Style.fill} />
            <html.a style={styles.link} onClick={() => setEditing(true)}>
              <i className="fa-regular fa-pen-to-square" />
            </html.a>
          </>
        )}
      </html.div>

      <Text as="p" style={styles.linkWrap}>
        {linkPart}
      </Text>

      <BuildEvents build={build} invalidate={invalidate} />
    </html.div>
  );
}

const styles = css.create({
  container: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#ddd',
    borderRadius: 8,
    paddingBlock: 10,
    paddingInline: 15,
    borderLeftWidth: 5,
    borderLeftColor: '#fff',
  },
  statusBorder: (status: string) => ({
    borderLeftColor:
      status === 'superseded'
        ? '#aaa'
        : status === 'qa'
        ? '#f55'
        : status === 'client'
        ? '#ffa13e'
        : status === 'releasing'
        ? 'rgb(33, 132, 193)'
        : status === 'live'
        ? 'rgb(49, 198, 93)'
        : '#fff',
  }),
  stats: {
    display: 'flex',
    gap: 25,
  },
  statLabel: {
    display: 'block',
    color: '#7f858b',
    marginBottom: 2,
  },
  statValue: {
    fontWeight: 'normal',
  },
  timestamp: {
    color: '#7f858b',
    fontSize: 12,
    marginTop: -3,
  },
  linkWrap: {
    marginTop: 8,
  },
  externalLink: {
    fontSize: 12,
  },
  link: {
    color: '#0366d6',
    cursor: 'pointer',
  },
  cancel: {
    color: 'inherit',
    cursor: 'pointer',
  },
});
